import React, { useMemo, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Avatar,
    Chip,
    TextField,
    Paper,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTopicLabel } from './useTopicLabel';
import {
    ComposedChart,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    BarChart,
    Bar,
    Cell,
} from 'recharts';
import { fetchDailyPoliticalMentions, fetchCandidateTopicOverTime } from '../../api/podcasts';
import {
    CANDIDATE_COLORS,
    TOPIC_COLORS,
    CANDIDATE_IMAGES,
    ELECTION_MILESTONES,
} from './electionColors';

interface ExploreTabProps {
    selectedRange: {
        from: string;
        to: string;
        defaultView: 'daily' | 'weekly';
    };
    includeRadio: boolean;
    isActive: boolean;
}

interface DailyMentionData {
    date: string;
    fullDate?: string;
    [candidate: string]: any;
}

// Helper to convert ISO date to YYYY-MM-DD for input
const toDateInputValue = (isoDate: string): string => {
    return isoDate.split('T')[0];
};

// Helper to convert YYYY-MM-DD to ISO date
const toISODate = (dateStr: string, isEnd: boolean = false): string => {
    if (isEnd) {
        return `${dateStr}T23:59:59.999Z`;
    }
    return `${dateStr}T00:00:00.000Z`;
};

export function ExploreTab({ selectedRange, includeRadio, isActive }: ExploreTabProps) {
    const theme = useTheme();
    const { t, i18n } = useTranslation('elecciones');
    const topicLabel = useTopicLabel();
    const [selectedCandidate, setSelectedCandidate] = useState<string>('');
    const [weeklyStatus, setWeeklyStatus] = useState<'daily' | 'weekly'>(selectedRange.defaultView);

    // Internal date state - initialized from selectedRange
    const [fromDate, setFromDate] = useState<string>(toDateInputValue(selectedRange.from));
    const [toDate, setToDate] = useState<string>(toDateInputValue(selectedRange.to));

    // Update dates when selectedRange changes
    useEffect(() => {
        setFromDate(toDateInputValue(selectedRange.from));
        setToDate(toDateInputValue(selectedRange.to));
    }, [selectedRange.from, selectedRange.to]);

    // Update weeklyStatus when selectedRange changes
    useEffect(() => {
        setWeeklyStatus(selectedRange.defaultView);
    }, [selectedRange.defaultView]);

    // Effective range for API calls
    const effectiveRange = useMemo(() => ({
        from: toISODate(fromDate),
        to: toISODate(toDate, true),
    }), [fromDate, toDate]);

    // Fetch daily mention data for candidates
    const { data: dailyMentions = [], isLoading: isLoadingMentions } = useQuery<DailyMentionData[]>({
        queryKey: ['daily-political-mentions-explore', effectiveRange.from, effectiveRange.to, includeRadio, weeklyStatus],
        queryFn: async () => {
            return fetchDailyPoliticalMentions({
                from: effectiveRange.from,
                to: effectiveRange.to,
                includeRadio,
                entityType: 'candidate',
                period: weeklyStatus,
            });
        },
        enabled: isActive,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
    });

    // Fetch topic distribution over time for selected candidate
    const { data: topicOverTimeData, isLoading: isLoadingTopics } = useQuery({
        queryKey: ['candidate-topic-over-time', effectiveRange.from, effectiveRange.to, includeRadio, weeklyStatus, selectedCandidate],
        queryFn: async () => {
            return fetchCandidateTopicOverTime({
                from: effectiveRange.from,
                to: effectiveRange.to,
                includeRadio,
                period: weeklyStatus,
                candidate: selectedCandidate,
            });
        },
        enabled: isActive && !!selectedCandidate,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
    });

    // Period comparison dates
    const PERIOD_1_END = '2025-11-16T03:59:59.999Z'; // Pre 1ra vuelta
    const PERIOD_2_START = '2025-11-16T04:00:00.000Z'; // Post 1ra vuelta
    const PERIOD_2_END = '2025-12-14T03:59:59.999Z'; // 2da vuelta

    // Fetch topic distribution for Period 1: from selected date to 1ra vuelta
    const { data: period1TopicData, isLoading: isLoadingPeriod1 } = useQuery({
        queryKey: ['candidate-topic-period1', effectiveRange.from, PERIOD_1_END, includeRadio, selectedCandidate],
        queryFn: async () => {
            return fetchCandidateTopicOverTime({
                from: effectiveRange.from,
                to: PERIOD_1_END,
                includeRadio,
                period: 'weekly',
                candidate: selectedCandidate,
            });
        },
        enabled: isActive && !!selectedCandidate,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
    });

    // Fetch topic distribution for Period 2: 1ra vuelta to 2da vuelta
    const { data: period2TopicData, isLoading: isLoadingPeriod2 } = useQuery({
        queryKey: ['candidate-topic-period2', PERIOD_2_START, PERIOD_2_END, includeRadio, selectedCandidate],
        queryFn: async () => {
            return fetchCandidateTopicOverTime({
                from: PERIOD_2_START,
                to: PERIOD_2_END,
                includeRadio,
                period: 'weekly',
                candidate: selectedCandidate,
            });
        },
        enabled: isActive && !!selectedCandidate,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
    });

    // Extract candidates list from mentions data
    const allCandidates = useMemo(() => {
        const candidatesSet = new Set<string>();
        dailyMentions.forEach(day => {
            Object.keys(day).forEach(key => {
                if (key !== 'date' && key !== 'fullDate' && day[key]?.count > 0) {
                    const cleanKey = key?.toString().trim();
                    if (cleanKey && cleanKey !== 'undefined' && cleanKey !== 'null') {
                        candidatesSet.add(cleanKey);
                    }
                }
            });
        });
        return Array.from(candidatesSet).sort((a, b) => {
            // Sort by total mentions
            const aMentions = dailyMentions.reduce((sum, day) => sum + (day[a]?.count || 0), 0);
            const bMentions = dailyMentions.reduce((sum, day) => sum + (day[b]?.count || 0), 0);
            return bMentions - aMentions;
        });
    }, [dailyMentions]);

    // Top 5 topics for this candidate + "otros"
    const candidateTopics = useMemo(() => {
        return topicOverTimeData?.topics || [];
    }, [topicOverTimeData]);

    // Process period comparison data for bar charts
    const periodComparisonData = useMemo(() => {
        const processTopicData = (data: typeof period1TopicData) => {
            if (!data?.data || !data?.topics) return [];

            // Aggregate percentages across all dates
            const topicTotals: Record<string, { total: number; count: number }> = {};

            data.topics.forEach(topic => {
                topicTotals[topic] = { total: 0, count: 0 };
            });

            data.data.forEach((item: Record<string, any>) => {
                data.topics.forEach(topic => {
                    // Access topic value directly (API returns percentages as topic keys)
                    const value = item[topic];
                    if (value !== undefined && typeof value === 'number') {
                        topicTotals[topic].total += value;
                        topicTotals[topic].count += 1;
                    }
                });
            });

            // Calculate averages and format for bar chart
            return data.topics
                .filter(t => t !== 'otros')
                .map(topic => ({
                    topic: topicLabel(topic),
                    percentage: topicTotals[topic].count > 0
                        ? Math.round((topicTotals[topic].total / topicTotals[topic].count) * 10) / 10
                        : 0,
                    color: TOPIC_COLORS[topic] || '#888',
                }))
                .sort((a, b) => b.percentage - a.percentage);
        };

        return {
            period1: processTopicData(period1TopicData),
            period2: processTopicData(period2TopicData),
        };
    }, [period1TopicData, period2TopicData, topicLabel]);

    // Auto-select first candidate when data loads
    useEffect(() => {
        if (allCandidates.length > 0 && !selectedCandidate) {
            setSelectedCandidate(allCandidates[0]);
        }
    }, [allCandidates, selectedCandidate]);

    // Build chart data merging mentions and topic distribution
    const chartData = useMemo(() => {
        if (!dailyMentions.length || !selectedCandidate) return [];

        // Create a map of topic data by date
        const topicDataByDate = new Map<string, any>();
        topicOverTimeData?.data.forEach(item => {
            topicDataByDate.set(item.date, item);
        });

        return dailyMentions.map(day => {
            const dateObj = new Date(day.date + 'T12:00:00.000Z');
            let formattedDate;
            if (weeklyStatus === 'weekly') {
                formattedDate = `${t('charts.weekShort')} ${dateObj.toLocaleDateString('en-GB', {
                    timeZone: 'America/Santiago',
                    day: 'numeric',
                    month: 'short'
                })}`;
            } else {
                formattedDate = dateObj.toLocaleDateString('en-GB', {
                    timeZone: 'America/Santiago',
                    day: 'numeric',
                    month: 'short'
                });
            }

            // Calculate total mentions for this period across all candidates
            let periodTotalMentions = 0;
            allCandidates.forEach(candidate => {
                periodTotalMentions += day[candidate]?.count || 0;
            });

            const mentionCount = day[selectedCandidate]?.count || 0;
            const mentionPct = periodTotalMentions > 0
                ? Math.round((mentionCount / periodTotalMentions) * 1000) / 10
                : 0;

            // Get topic distribution for this date
            const topicData = topicDataByDate.get(day.date) || {};

            // Build result with topic percentages scaled to candidate's mention %
            const result: any = {
                date: formattedDate,
                fullDate: day.date,
                mentions: mentionPct,
                mentionCount,
                totalMentions: periodTotalMentions,
                candidateTotalMentions: topicData.total || mentionCount,
            };

            // Add each topic's percentage (scaled to candidate's total %)
            // e.g., if candidate has 50% of mentions and topic is 20% of candidate's mentions,
            // display as 10% (50% * 20%)
            candidateTopics.forEach(topic => {
                const topicPctOfCandidate = topicData[topic] || 0; // % of candidate's mentions
                const scaledPct = (mentionPct * topicPctOfCandidate) / 100;
                result[topic] = Math.round(scaledPct * 10) / 10;
                result[`${topic}_raw`] = topicPctOfCandidate; // Keep raw % for tooltip
            });

            return result;
        });
    }, [dailyMentions, selectedCandidate, topicOverTimeData, weeklyStatus, allCandidates, candidateTopics]);

    // Calculate statistics for selected candidate
    const candidateStats = useMemo(() => {
        if (!selectedCandidate || !chartData.length) return null;

        const totalMentionCount = chartData.reduce((sum, d) => sum + (d.mentionCount || 0), 0);
        const avgPct = chartData.length > 0
            ? Math.round(chartData.reduce((sum, d) => sum + d.mentions, 0) / chartData.length * 10) / 10
            : 0;

        // Calculate top topic
        const topicTotals = new Map<string, number>();
        candidateTopics.forEach(topic => {
            const total = chartData.reduce((sum, d) => sum + (d[`${topic}_raw`] || 0), 0);
            topicTotals.set(topic, total);
        });
        const sortedTopics = Array.from(topicTotals.entries())
            .filter(([t]) => t !== 'otros')
            .sort((a, b) => b[1] - a[1]);
        const topTopic = sortedTopics[0]?.[0] || null;

        return {
            totalMentions: totalMentionCount,
            avgPct,
            topTopic,
            topicsCount: candidateTopics.length - 1, // Exclude "otros"
        };
    }, [selectedCandidate, chartData, candidateTopics]);

    const isLoading = isLoadingMentions || isLoadingTopics;

    // Helper to render the chart content based on loading/data state
    const renderChartContent = () => {
        if (isLoading && !dailyMentions.length) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!dailyMentions.length) {
            return (
                <Alert severity="info">
                    {t('empty.noPeriod')}
                </Alert>
            );
        }

        if (!selectedCandidate) {
            return (
                <Alert severity="info">
                    {t('empty.pickCandidate')}
                </Alert>
            );
        }

        return null; // Will render the actual chart
    };

    return (
        <Box>
            {/* Controls */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                {/* Date From */}
                <TextField
                    label={t('period.from')}
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                    size="small"
                />

                {/* Date To */}
                <TextField
                    label={t('period.to')}
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                    size="small"
                />

                {/* Candidate Selector */}
                <FormControl sx={{ minWidth: 250 }} size="small">
                    <InputLabel>{t('fields.candidate')}</InputLabel>
                    <Select
                        value={selectedCandidate}
                        onChange={(e) => setSelectedCandidate(e.target.value)}
                        input={<OutlinedInput label={t('fields.candidate')} />}
                        renderValue={(selected) => (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar
                                    src={CANDIDATE_IMAGES[selected]}
                                    alt={selected}
                                    sx={{ width: 24, height: 24 }}
                                >
                                    {selected.charAt(0)}
                                </Avatar>
                                <Typography>{selected}</Typography>
                            </Stack>
                        )}
                    >
                        {allCandidates.map((candidate) => (
                            <MenuItem key={candidate} value={candidate}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Avatar
                                        src={CANDIDATE_IMAGES[candidate]}
                                        alt={candidate}
                                        sx={{ width: 24, height: 24 }}
                                    >
                                        {candidate.charAt(0)}
                                    </Avatar>
                                    <Typography>{candidate}</Typography>
                                </Stack>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Period Selector */}
                <FormControl sx={{ minWidth: 150 }} size="small">
                    <InputLabel>{t('period.label')}</InputLabel>
                    <Select
                        value={weeklyStatus}
                        onChange={(e) => setWeeklyStatus(e.target.value as 'daily' | 'weekly')}
                        input={<OutlinedInput label={t('period.label')} />}
                    >
                        <MenuItem value="daily">{t('cadence.daily')}</MenuItem>
                        <MenuItem value="weekly">{t('cadence.weekly')}</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {/* Chart Area - shows loader, empty state, or actual chart */}
            {renderChartContent() || (
                <>
                    {/* Stats Summary */}
                    {candidateStats && (
                        <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                            <Card sx={{ flex: 1, minWidth: 150 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Avatar
                                            src={CANDIDATE_IMAGES[selectedCandidate]}
                                            alt={selectedCandidate}
                                            sx={{ width: 32, height: 32 }}
                                        >
                                            {selectedCandidate.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">{t('fields.candidate')}</Typography>
                                            <Typography variant="body1" fontWeight={600}>{selectedCandidate}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: 1, minWidth: 150 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">{t('explore.avgShare')}</Typography>
                                    <Typography variant="h5">{candidateStats.avgPct}%</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: 1, minWidth: 150 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">{t('breakdown.totalMentions')}</Typography>
                                    <Typography variant="h5">{candidateStats.totalMentions.toLocaleString()}</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: 1, minWidth: 150 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">{t('explore.topTopic')}</Typography>
                                    <Typography variant="h6">
                                        {candidateStats.topTopic ? (
                                            <Chip
                                                label={candidateStats.topTopic}
                                                size="small"
                                                sx={{ backgroundColor: TOPIC_COLORS[candidateStats.topTopic] || '#888', color: 'white' }}
                                            />
                                        ) : 'N/A'}
                                    </Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: 1, minWidth: 150 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">{t('explore.mainTopics')}</Typography>
                                    <Typography variant="h5">{candidateStats.topicsCount}</Typography>
                                </CardContent>
                            </Card>
                        </Stack>
                    )}

                    {/* Combined Chart */}
                    <Card sx={{
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid', borderColor: 'divider',
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ mb: 3 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        letterSpacing: '-0.02em',
                                        mb: 0.5
                                    }}
                                >
                                    {t('explore.title', { candidate: selectedCandidate })}
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                                    {t('explore.subtitle')}
                                </Typography>
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: 'action.hover',
                                    borderRadius: 2,
                                    p: 2,
                                    border: '1px solid', borderColor: 'divider'
                                }}
                            >
                                <Box sx={{ width: '100%', height: 420 }}>
                                    <ResponsiveContainer>
                                        <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                                            <defs>
                                                <linearGradient id="mentionLineGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={CANDIDATE_COLORS[selectedCandidate] || '#1565C0'} stopOpacity={1} />
                                                    <stop offset="100%" stopColor={CANDIDATE_COLORS[selectedCandidate] || '#1565C0'} stopOpacity={0.6} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />

                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                                tickLine={{ stroke: theme.palette.divider }}
                                                axisLine={{ stroke: theme.palette.divider }}
                                                interval="preserveStartEnd"
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                                                tickLine={{ stroke: theme.palette.divider }}
                                                axisLine={{ stroke: theme.palette.divider }}
                                                domain={[0, 100]}
                                                ticks={[0, 25, 50, 75, 100]}
                                                tickFormatter={(value) => `${value}%`}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                                    border: '1px solid', borderColor: 'divider',
                                                    borderRadius: 12,
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                                    padding: '12px 16px'
                                                }}
                                                labelStyle={{ fontWeight: 600, marginBottom: 8, color: theme.palette.text.primary }}
                                                itemStyle={{ padding: '2px 0' }}
                                                formatter={(value, name, props) => {
                                                    if (name === t('explore.mentionShare')) {
                                                        const count = props.payload.mentionCount || 0;
                                                        return [t('explore.tooltipShare', { value, n: count }), t('explore.tooltipShareLabel')];
                                                    }
                                                    // For topic areas, show both scaled and raw percentage
                                                    const rawPct = props.payload[`${name}_raw`];
                                                    if (rawPct !== undefined) {
                                                        return [t('explore.tooltipTopic', { value, raw: rawPct, candidate: selectedCandidate }), name];
                                                    }
                                                    return [`${value}%`, name];
                                                }}
                                                labelFormatter={(label, payload) => {
                                                    if (payload && payload[0]) {
                                                        const dateStr = payload[0].payload.fullDate;
                                                        const dateObj = new Date(dateStr + 'T12:00:00.000Z');
                                                        const totalMentions = payload[0].payload.totalMentions || 0;
                                                        const candidateMentions = payload[0].payload.candidateTotalMentions || 0;
                                                        const formattedDate = dateObj.toLocaleDateString(i18n.language.startsWith('en') ? 'en-GB' : 'es-CL', {
                                                            timeZone: 'America/Santiago',
                                                            weekday: 'long',
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric'
                                                        });
                                                        return t('explore.tooltipPoint', {
                                                            date: formattedDate,
                                                            total: totalMentions,
                                                            candidateCount: candidateMentions,
                                                            candidate: selectedCandidate,
                                                        });
                                                    }
                                                    return label;
                                                }}
                                            />
                                            <Legend />

                                            {/* Election milestone reference lines */}
                                            {ELECTION_MILESTONES.map((milestone) => {
                                                const matchingPoint = chartData.find(p => {
                                                    const targetDate = new Date(milestone.date + 'T12:00:00.000Z');
                                                    const pointDate = new Date(p.fullDate + 'T12:00:00.000Z');
                                                    if (weeklyStatus === 'weekly') {
                                                        const weekEnd = new Date(pointDate);
                                                        weekEnd.setDate(weekEnd.getDate() + 6);
                                                        return targetDate >= pointDate && targetDate <= weekEnd;
                                                    }
                                                    return p.fullDate === milestone.date;
                                                });
                                                if (!matchingPoint) return null;
                                                return (
                                                    <ReferenceLine
                                                        key={milestone.date}
                                                        x={matchingPoint.date}
                                                        stroke={milestone.color}
                                                        strokeWidth={2}
                                                        strokeDasharray="4 4"
                                                        label={{
                                                            value: t(`milestones.${milestone.id}.short`),
                                                            position: 'top',
                                                            fill: milestone.color,
                                                            fontSize: 10,
                                                        }}
                                                    />
                                                );
                                            })}

                                            {/* Stacked areas for topic distribution */}
                                            {candidateTopics.map((topic, index) => (
                                                <Area
                                                    key={topic}
                                                    type="monotone"
                                                    dataKey={topic}
                                                    name={topicLabel(topic)}
                                                    stackId="1"
                                                    fill={TOPIC_COLORS[topic] || `hsl(${index * 60}, 70%, 50%)`}
                                                    stroke={TOPIC_COLORS[topic] || `hsl(${index * 60}, 70%, 40%)`}
                                                    fillOpacity={0.7}
                                                />
                                            ))}

                                            {/* Mentions line on top */}
                                            <Line
                                                type="monotone"
                                                dataKey="mentions"
                                                name={t('explore.mentionShare')}
                                                stroke={CANDIDATE_COLORS[selectedCandidate] || '#1976D2'}
                                                strokeWidth={3}
                                                dot={{ r: 4, fill: CANDIDATE_COLORS[selectedCandidate] || '#1976D2' }}
                                                activeDot={{ r: 6 }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Paper>

                            {/* Topic Legend */}
                            {candidateTopics.length > 0 && (
                                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                    {candidateTopics.filter(t => t !== 'otros').map((topic, index) => (
                                        <Chip
                                            key={topic}
                                            label={topicLabel(topic)}
                                            size="small"
                                            sx={{
                                                backgroundColor: TOPIC_COLORS[topic] || `hsl(${index * 60}, 70%, 50%)`,
                                                color: 'white'
                                            }}
                                        />
                                    ))}
                                    <Chip
                                        label={t('explore.otherTopics')}
                                        size="small"
                                        sx={{
                                            backgroundColor: TOPIC_COLORS['otros'],
                                            color: 'white'
                                        }}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Period Comparison Section */}
                    {selectedCandidate && (
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 3,
                                borderRadius: 3,
                                border: '1px solid', borderColor: 'divider',
                                bgcolor: 'background.paper',
                                overflow: 'hidden',
                            }}
                        >
                            <Box sx={{
                                px: 3,
                                py: 2.5,
                                borderBottom: 1, borderColor: 'divider',
                                bgcolor: 'action.hover',
                            }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        color: theme.palette.text.primary,
                                        fontSize: '1.1rem',
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {t('explore.compareTitle', { candidate: selectedCandidate })}
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                                    {t('explore.beforeAfter')}
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                    {/* Period 1: Pre 1ra Vuelta */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid', borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                textAlign: 'center',
                                                mb: 0.5,
                                            }}
                                        >
                                            {t('period.beforeFirstRound')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', textAlign: 'center', mb: 2 }}>
                                            Desde inicio hasta 16 Nov 2025
                                        </Typography>
                                        {isLoadingPeriod1 ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress size={30} />
                                            </Box>
                                        ) : periodComparisonData.period1.length > 0 ? (
                                            <Box sx={{ width: '100%', height: 250 }}>
                                                <ResponsiveContainer>
                                                    <BarChart
                                                        data={periodComparisonData.period1}
                                                        layout="vertical"
                                                        margin={{ top: 5, right: 20, left: 100, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={true} vertical={false} />
                                                        <XAxis
                                                            type="number"
                                                            domain={[0, 'auto']}
                                                            tickFormatter={(v) => `${v}%`}
                                                            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                                            tickLine={{ stroke: theme.palette.divider }}
                                                            axisLine={{ stroke: theme.palette.divider }}
                                                        />
                                                        <YAxis
                                                            type="category"
                                                            dataKey="topic"
                                                            tick={{ fontSize: 11, fill: theme.palette.text.primary }}
                                                            tickLine={false}
                                                            axisLine={false}
                                                            width={95}
                                                        />
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                                                border: 'none',
                                                                borderRadius: 12,
                                                                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                                                padding: '12px 16px',
                                                            }}
                                                            formatter={(value) => [`${value}%`, t('explore.average')]}
                                                        />
                                                        <Bar dataKey="percentage" radius={[0, 6, 6, 0]}>
                                                            {periodComparisonData.period1.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        ) : (
                                            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                                {t('empty.noSlice')}
                                            </Alert>
                                        )}
                                    </Paper>

                                    {/* Period 2: Entre 1ra y 2da Vuelta */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid', borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                textAlign: 'center',
                                                mb: 0.5,
                                            }}
                                        >
                                            {t('period.betweenRounds')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', textAlign: 'center', mb: 2 }}>
                                            16 Nov - 14 Dic 2025
                                        </Typography>
                                        {isLoadingPeriod2 ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                                <CircularProgress size={30} />
                                            </Box>
                                        ) : periodComparisonData.period2.length > 0 ? (
                                            <Box sx={{ width: '100%', height: 250 }}>
                                                <ResponsiveContainer>
                                                    <BarChart
                                                        data={periodComparisonData.period2}
                                                        layout="vertical"
                                                        margin={{ top: 5, right: 20, left: 100, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={true} vertical={false} />
                                                        <XAxis
                                                            type="number"
                                                            domain={[0, 'auto']}
                                                            tickFormatter={(v) => `${v}%`}
                                                            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                                            tickLine={{ stroke: theme.palette.divider }}
                                                            axisLine={{ stroke: theme.palette.divider }}
                                                        />
                                                        <YAxis
                                                            type="category"
                                                            dataKey="topic"
                                                            tick={{ fontSize: 11, fill: theme.palette.text.primary }}
                                                            tickLine={false}
                                                            axisLine={false}
                                                            width={95}
                                                        />
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary,
                                                                border: 'none',
                                                                borderRadius: 12,
                                                                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                                                padding: '12px 16px',
                                                            }}
                                                            formatter={(value) => [`${value}%`, t('explore.average')]}
                                                        />
                                                        <Bar dataKey="percentage" radius={[0, 6, 6, 0]}>
                                                            {periodComparisonData.period2.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        ) : (
                                            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                                {t('empty.noSlice')}
                                            </Alert>
                                        )}
                                    </Paper>
                                </Stack>
                            </Box>
                        </Paper>
                    )}

                    {/* Info */}
                    {candidateTopics.length === 0 && !isLoading && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('empty.noDistribution')}
                        </Alert>
                    )}
                </>
            )}
        </Box>
    );
}

